"use client";

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

// Sekmeler (WAI-ARIA "tabs" deseni): tablist/tab/tabpanel; ←/→, Home/End ile gezinme, seçili sekme sekme sırasında
// (roving tabindex). Yalnızca seçili panelin içeriği çizilir; diğerleri ilk seçildiklerinde eklenir ve sonra yerinde
// kalır (tekrar indirme yok: içerik sunucu bileşeni olarak zaten sayfa yükünde). Ana sayfada gizli 6 bölge sekmesi
// ~1.200 DOM öğesi ve 20+ kartın hidrasyonu demekti (Lighthouse "DOM boyutu", mobil TBT). JavaScript olmadan ilk panel
// görünür (önceden de yalnızca o görünürdü).

export type TabItem = { key: string; label: ReactNode; panel: ReactNode };

export function Tabs({ items, label, className = "" }: { items: TabItem[]; label: string; className?: string }) {
  const [active, setActive] = useState(items[0]?.key);
  const [visited, setVisited] = useState<ReadonlySet<string | undefined>>(() => new Set([items[0]?.key]));
  if (!visited.has(active)) setVisited(new Set([...visited, active]));
  const base = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const last = items.length - 1;
    const next = e.key === "ArrowRight" ? (i === last ? 0 : i + 1) : e.key === "ArrowLeft" ? (i === 0 ? last : i - 1) : e.key === "Home" ? 0 : e.key === "End" ? last : null;
    if (next === null) return;
    e.preventDefault();
    setActive(items[next].key);
    refs.current[next]?.focus();
  };

  return (
    <div className={className}>
      <div role="tablist" aria-label={label} className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
        {items.map((t, i) => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${t.key}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${t.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(t.key)}
              onKeyDown={(e) => onKey(e, i)}
              className={`inline-flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-semibold transition-colors ${
                selected ? "bg-primary text-on-primary" : "bg-surface text-ink ring-1 ring-inset ring-line-strong hover:bg-primary-soft"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {items.map((t) => (
        <div
          key={t.key}
          role="tabpanel"
          id={`${base}-panel-${t.key}`}
          aria-labelledby={`${base}-tab-${t.key}`}
          hidden={t.key !== active}
          className="mt-8"
        >
          {visited.has(t.key) && t.panel}
        </div>
      ))}
    </div>
  );
}
